import React, { useEffect, useState } from 'react'
import Navigation from '../partials/Navigation'
import useTransferList from '../hooks/useTransferList'
import { Transfer } from '../types'
import { TransferDetail, TransferSummaryListItem } from '../components/TransferSummaryList'
import ListGroup from '../components/ListGroup'
import Tabs from '../components/Tabs'
import useAxios from '../hooks/useAxios'
import Page from '../components/Page'
import pluralize from 'pluralize'
import Drawer from '../components/Drawer'
import NavigationArrow from '../components/NavigationArrow'
import { formatCurrency } from '../common'
import { logError } from '../store/slices/app'
import { useDispatch } from 'react-redux'

type TabTypes = 'future' | 'past'

const MyTransfers = () => {
  const [tab, setTab] = useState<TabTypes>('future')
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | undefined>(undefined)
  const [cancelDrawerVisible, setCancelDrawerVisible] = useState(false)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const api = useTransferList(tab)
  const instance = useAxios()
  const dispatch = useDispatch()

  useEffect(() => {
    if (api.isLoading || selectedTransfer !== undefined) {
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const idx = api.response.transfers.findIndex((x: Transfer) => x.booking_id === urlParams.get('booking_id'))

    setSelectedTransfer(api.response.transfers[idx === -1 ? 0 : idx])
  }, [api])

  const handleSelectTransfer = (item: Transfer) => {
    setSelectedTransfer(item)
    setDrawerVisible(true)
  }

  const handleCloseDetails = () => {
    setDrawerVisible(false)
    setSelectedTransfer(undefined)
  }

  const handleCloseCancelDrawer = () => {
    setCancelDrawerVisible(false)
  }

  const handleCancelTransfer = async () => {
    if (isLoading || !selectedTransfer) return

    try {
      setIsLoading(true)
      await instance.delete(`ac/v1/transfers/${selectedTransfer.booking_id}`)
      await api.refresh()
      setSelectedTransfer(undefined)
      setCancelDrawerVisible(false)
    } catch (err) {
      dispatch(logError(err))
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisplayType = (type: string) => {
    setTab(type as TabTypes)

    setSelectedTransfer(undefined)
  }

  const onUpdated = async (transfer: Transfer) => {
    setSelectedTransfer(transfer)

    await api.refresh()
  }
  return (
    <Page className="bg-white pb-6">
      <Navigation />

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10 pt-4 pt-lg-5">
            <h2>My transfers</h2>
            <Tabs
              className="mt-4"
              selected={tab}
              tabs={{ future: 'Upcoming', past: 'Past' }}
              onSelect={handleDisplayType}
            />
            <div className="my-4">
              {api.isLoading ? (
                <span>Loading transfers...</span>
              ) : (
                <span>
                  You have <strong>{api.response.transfers.length}</strong> {tab === 'past' ? 'past' : 'upcoming'}{' '}
                  {pluralize('transfer', api.response.transfers.length)}
                </span>
              )}
            </div>

            {!api.isLoading && api.response.transfers.length > 0 && (
              <div className="row">
                <div className="col-12 col-lg-6">
                  {/*<ListGroup.Title>Apr, 2020</ListGroup.Title>*/}
                  <ListGroup.Items>
                    {api.response.transfers.map((item: Transfer, idx: number) => (
                      <ListGroup.Item hover isActive={selectedTransfer?.booking_id === item.booking_id} key={idx}>
                        <TransferSummaryListItem
                          transfer={item}
                          key={idx}
                          onSelect={handleSelectTransfer}
                          icon
                          isActive={selectedTransfer?.booking_id === item.booking_id}
                        />
                      </ListGroup.Item>
                    ))}
                  </ListGroup.Items>
                </div>
                {selectedTransfer && (
                  <>
                    <div className="d-none d-lg-block col-12 col-lg-6">
                      <TransferDetail
                        onUpdated={onUpdated}
                        transfer={selectedTransfer}
                        onCancel={() => setCancelDrawerVisible(true)}
                      />
                    </div>
                  </>
                )}

                <Drawer visible={drawerVisible} onBackgroundClick={handleCloseDetails} className="d-lg-none">
                  {selectedTransfer && (
                    <>
                      <NavigationArrow onClick={handleCloseDetails} />
                      <div className="px-4 pb-6">
                        <TransferDetail
                          onUpdated={onUpdated}
                          transfer={selectedTransfer}
                          onCancel={() => setCancelDrawerVisible(true)}
                        />
                      </div>
                    </>
                  )}
                </Drawer>

                <Drawer visible={cancelDrawerVisible} onBackgroundClick={handleCloseCancelDrawer}>
                  {selectedTransfer && (
                    <>
                      <NavigationArrow onClick={handleCloseCancelDrawer} />
                      <div className="px-4 pb-6">
                        <h3>Confirm cancellation</h3>
                        <p>
                          Are you sure you wish to cancel this transfer? You will receive a refund of £
                          {formatCurrency(selectedTransfer.refund_amount as number)}
                        </p>

                        <div className="d-flex flex-column flex-sm-row-reverse">
                          <button className="btn btn-secondary" onClick={handleCancelTransfer} disabled={isLoading}>
                            {isLoading && (
                              <div className="spinner-border spinner-border-sm text-light mr-2" role="status"></div>
                            )}
                            Cancel transfer
                          </button>
                          <button className="btn btn-link" onClick={handleCloseCancelDrawer}>
                            Keep transfer
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </Drawer>
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  )
}

export default MyTransfers
